import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { AppResponsiveContainer } from 'components/containers/AppResponsiveContainer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SecurityIcon from '@mui/icons-material/Security';
import HandshakeIcon from '@mui/icons-material/Handshake';

const DocumentationPage = () => {
    const [expanded, setExpanded] = useState(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const documentSections = [
        {
            icon: <DescriptionIcon fontSize="large" />,
            title: 'Service Overview',
            content: 'Comprehensive chassis transport solutions across India with advanced tracking and security features.'
        },
        {
            icon: <AssignmentIcon fontSize="large" />,
            title: 'Documentation',
            content: 'Complete guide to required paperwork, permits, and compliance documents.'
        },
        {
            icon: <SecurityIcon fontSize="large" />,
            title: 'Safety Standards',
            content: 'Detailed safety protocols and industry-standard compliance measures.'
        },
        {
            icon: <HandshakeIcon fontSize="large" />,
            title: 'Client Guidelines',
            content: 'Essential information for clients about booking, tracking, and support services.'
        }
    ];

    const vehicleTypes = [
        { type: 'Light Chassis', maxLength: '20ft', maxWeight: '7,500 kg', specialFeatures: 'Quick loading' },
        { type: 'Medium Chassis', maxLength: '30ft', maxWeight: '15,000 kg', specialFeatures: 'Versatile usage' },
        { type: 'Heavy Chassis', maxLength: '40ft', maxWeight: '25,000 kg', specialFeatures: 'Heavy-duty support' },
        { type: 'Extra Heavy', maxLength: '50ft', maxWeight: '40,000 kg', specialFeatures: 'Special permits required' }
    ];

    const documentationRequirements = [
        {
            title: 'Vehicle Documentation',
            items: [
                'Vehicle Registration Certificate',
                'Fitness Certificate',
                'Insurance Documentation',
                'Route Permit Details',
                'Pollution Control Certificate'
            ]
        },
        {
            title: 'Transport Documentation',
            items: [
                'Loading Bill',
                'Way Bill',
                'Delivery Challan',
                'GST E-way Bill',
                'Transit Insurance'
            ]
        },
        {
            title: 'Client Documentation',
            items: [
                'Purchase Order',
                'Authorization Letter',
                'Indemnity Bond',
                'Quality Check Certificate',
                'Delivery Acceptance Form'
            ]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                py: 6,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography variant="h2" gutterBottom>
                                Documentation Center
                            </Typography>
                            <Typography variant="h5">
                                Complete Guide to Our Chassis Transport Solutions
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Main Documentation Sections */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <AppResponsiveContainer spacing={4}>
                    {documentSections.map((section, index) => (
                        <Card key={index} sx={{ height: '100%' }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                p: 3
                            }}>
                                <Box color="primary.main" mb={2} sx={{ transform: 'scale(1.5)' }}>
                                    {section.icon}
                                </Box>
                                <Typography variant="h5" gutterBottom>
                                    {section.title}
                                </Typography>
                                <Typography color="text.secondary">
                                    {section.content}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </AppResponsiveContainer>
            </Container>

            {/* Vehicle Types Section */}
            <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" align="center" gutterBottom>
                        Supported Vehicle Types
                    </Typography>
                    <TableContainer component={Paper} sx={{ mt: 4 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Max Length</strong></TableCell>
                                    <TableCell><strong>Max Weight</strong></TableCell>
                                    <TableCell><strong>Special Features</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vehicleTypes.map((vehicle, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{vehicle.type}</TableCell>
                                        <TableCell>{vehicle.maxLength}</TableCell>
                                        <TableCell>{vehicle.maxWeight}</TableCell>
                                        <TableCell>{vehicle.specialFeatures}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Container>
            </Box>

            {/* Documentation Requirements */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Required Documentation
                </Typography>
                <Box sx={{ mt: 4 }}>
                    {documentationRequirements.map((section, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === `panel${index}`}
                            onChange={handleAccordionChange(`panel${index}`)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">{section.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    {section.items.map((item, idx) => (
                                        <Grid item xs={12} sm={6} key={idx}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography>
                                                    <VerifiedIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                    {item}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Container>

            {/* Download Section */}
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                    <CardContent sx={{ py: 4 }}>
                        <Typography variant="h5" gutterBottom>
                            Download Complete Documentation
                        </Typography>
                        <Typography paragraph>
                            Get detailed information about our services, requirements, and procedures
                        </Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<DescriptionIcon />}
                            sx={{ mt: 2 }}
                        >
                            Download PDF Guide
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default DocumentationPage;